import { CircularProgress, useTheme } from "@mui/material";
import { ModeGoodsOut20Px } from "@qubit/autoparts";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";
import { useShowPickToLightFlagged } from "~/features/autostorePicking/hooks/useShowPickToLightFlagged";
import { StartPickingModal } from "~/features/startPickingModal/StartPickingModal";

import { useConfigurePortsWithRetry } from "~/hooks/useConfigurePortsWithRetry";
import { useDisclosure } from "~/hooks/useDisclosure";
import {
  getCompartmentId,
  parentPortIdFoundInWorkstation
} from "~/lib/helpers";

import { mixpanelTrack, useMixpanelPageName } from "~/lib/mixpanel-tracking";

import {
  noBatchesLeftMessage,
  setCartNumberConfirmed,
  setShowTwoBinComponents,
  startPickingOnWorkstation
} from "~/redux/actions";
import { clearUserMessage } from "~/redux/actions/site";
import { sendUserTaskSelectedEvent } from "~/redux/actions/userEvents";
import { selectServerConfig } from "~/redux/selectors/appSelectors";
import { selectUserCanPick } from "~/redux/selectors/authSelectors";
import { selectShowTwoBinComponents } from "~/redux/selectors/autostoreSelectors";
import { selectUserMessages } from "~/redux/selectors/siteSelectors";
import {
  selectWorkstationAutostoreGridId,
  selectSelectedPortId,
  selectThisWorkstation,
  selectWorkstationHasPickingRole
} from "~/redux/selectors/workstationsSelectors";

import { MainButton } from "./MainButton";

export function PickingButton() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { palette } = useTheme();
  const trackedPageName = useMixpanelPageName();

  const fusionPortScreenEnabled =
    useAppSelector(selectServerConfig).ap_fusionPortScreenEnabled;

  const canUserPick = useAppSelector(selectUserCanPick);

  const isPickingRoleFound = useAppSelector(selectWorkstationHasPickingRole);
  const pickToLightEnabled =
    useAppSelector(selectServerConfig).ap_showPickToLight;
  const siteWorkstation = useAppSelector(selectThisWorkstation);
  const selectedAutostoreGridId = useAppSelector(
    selectWorkstationAutostoreGridId
  );
  const showTwoBinComponents = useAppSelector(selectShowTwoBinComponents);
  const sitePortId = useAppSelector(selectSelectedPortId);
  const userMessages = useAppSelector(selectUserMessages);
  const {
    open: startPickingModalIsOpen,
    onClose: onCloseStartPickingModal,
    onOpen: onOpenStartPickingModal
  } = useDisclosure();

  const {
    configurePortsWithRetry,
    isConfigurePortsRetrying,
    multiPortRequestDuration
  } = useConfigurePortsWithRetry({
    workstationMode: "Picking",
    retryThreshold: 1000
  });

  const [startPickingLoading, setStartPickingLoading] =
    useState<boolean>(false);

  const disablePicking = !canUserPick || !isPickingRoleFound;

  const handlePickingButtonClick = () => {
    if (!!selectedAutostoreGridId && !!sitePortId && isPickingRoleFound) {
      onOpenStartPickingModal();
    }
    mixpanelTrack({
      type: "Button Click",
      label: "Picking Button",
      trackedPageName
    });
  };

  const { showPickToLightFlagged } = useShowPickToLightFlagged();

  // On the Autostore Picking page, we have a part of code which redirects user
  // To the Autostore Main page if 'No batches are available to pick' error exists.
  // With new stack messages feature, we're saving all messages (including above mentioned error) and when we click
  // On the pick new batch button on the Start Picking Modal, Qubit will redirect immediately to the Autostore main page
  // Even if we have batch ready. So we need to erase this message when user tries to start picking process
  const noBatchesfoundWarningMessage = userMessages?.find(
    (message) => message.title === noBatchesLeftMessage
  );

  const handlePickClick = async () => {
    dispatch(setCartNumberConfirmed(false));
    setStartPickingLoading(true);
    try {
      let batchId: string | null = null;

      if (fusionPortScreenEnabled) {
        const isConfigurePortsSuccess = await configurePortsWithRetry();
        if (!isConfigurePortsSuccess) {
          return null;
        }

        const enableMultiPort = parentPortIdFoundInWorkstation(
          siteWorkstation?.ports
        );

        const pickingStateLocal = await dispatch(
          startPickingOnWorkstation({
            enableMultiPort: enableMultiPort || undefined
          })
        );
        batchId = pickingStateLocal?.batchId ?? null;

        if (
          pickingStateLocal &&
          pickToLightEnabled &&
          selectedAutostoreGridId &&
          sitePortId
        ) {
          const compartmentId = getCompartmentId(
            pickingStateLocal.binConfiguration?.configurationType || 1,
            pickingStateLocal.currentPicks[0]?.pickBin
              ?.autostoreCompartmentNumber || 1
          );
          await showPickToLightFlagged(
            selectedAutostoreGridId,
            sitePortId,
            pickingStateLocal.currentPicks[0]?.pickBin?.autostoreBin
              ?.autostoreBinId || 1,
            compartmentId,
            pickingStateLocal.currentPicks.length
              ? pickingStateLocal.currentPicks[0].quantity.value
              : 1
          );
        }

        if (showTwoBinComponents !== enableMultiPort) {
          dispatch(setShowTwoBinComponents(enableMultiPort));
        }
      } else {
        const pickingStateLocal = await dispatch(startPickingOnWorkstation());

        batchId = pickingStateLocal?.batchId ?? null;
      }

      if (noBatchesfoundWarningMessage) {
        dispatch(clearUserMessage(noBatchesfoundWarningMessage.id));
      }

      await dispatch(sendUserTaskSelectedEvent("PICKING"));
      onCloseStartPickingModal();

      return batchId;
    } catch {
      return null;
    } finally {
      setStartPickingLoading(false);
    }
  };

  const hasWorkstation = !!siteWorkstation;

  const alert =
    (!hasWorkstation && t("no workstation selected")) ||
    (!isPickingRoleFound && hasWorkstation && t("no picking role found")) ||
    (!canUserPick && t("user has insufficient privileges")) ||
    undefined;

  const alertKind =
    (!hasWorkstation && "workstation") ||
    (!isPickingRoleFound && hasWorkstation && "workstation") ||
    (!canUserPick && "user") ||
    undefined;

  return (
    <>
      <MainButton
        alert={alert}
        alertKind={alertKind}
        disabled={disablePicking}
        onClick={handlePickingButtonClick}
        icon={
          startPickingLoading ? (
            <CircularProgress size={30} color="primary" />
          ) : (
            <ModeGoodsOut20Px
              width="30px"
              height="30px"
              fill={palette.primary.contrastText}
            />
          )
        }
      >
        {t("nav.link.picking")}
      </MainButton>

      <StartPickingModal
        open={startPickingModalIsOpen}
        onClose={onCloseStartPickingModal}
        onHomeButtonClickCallback={onCloseStartPickingModal}
        onNextPickButtonClickCallback={handlePickClick}
        maxWidth="sm"
        title={`${t("start picking")}?`}
        pickButtonText={t("start")}
        isConfigurePortsRetrying={isConfigurePortsRetrying}
        multiPortRequestDuration={multiPortRequestDuration}
      />
    </>
  );
}
